import { Globe, MoreHorizontal, Settings2 } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Page, PageHeader } from "@/components/layout/page";
import { DomainOutboundDialog } from "@/features/domain-outbound-form";
import { DomainRetentionCard } from "@/features/domain-retention";
import { DomainTranscriptionCard } from "@/features/domain-transcription";
import { useAuth } from "@/lib/auth";
import { useDomains } from "@/lib/queries";
import { atLeast, type Domain } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * SIP domains, near enough read-only in the console.
 *
 * A realm itself is switch configuration, created by `make seed-switch` from
 * `config/switch.json` — that file is the source of truth and is version-controlled alongside
 * the trunks and profiles it sits with. A create button here would put the console and the seed
 * file in a race to define the same realm.
 *
 * Outbound routing is the exception, and is editable by admins: which carrier a domain's calls
 * leave on is a business decision, not a switch-topology one. The column shows what the switch
 * would actually use — the resolved trunks, not the saved ids — so a domain whose chosen trunks
 * have all been disabled reads as broken here instead of looking configured. Whether a domain's
 * recordings are transcribed is the other business decision here, and admin's too. How long a
 * domain keeps its recordings and transcripts is the third: an admin sets it, and everyone who can
 * open this page reads it, because "how long do you keep my call" gets asked of supervisors too.
 */
export function DomainsPage() {
  const { user: actor } = useAuth();
  const canManage = atLeast(actor?.role, "admin");
  const { data, isPending, error, refetch } = useDomains();

  const [routing, setRouting] = React.useState<Domain | undefined>();

  return (
    <Page>
      <PageHeader
        title="Domains"
        description="The SIP realms endpoints register in. Managed as switch configuration; outbound routing, transcription and how long calls are kept are set here."
      />

      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load domains"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Globe}
            title="No domains"
            description="Seed the switch configuration first: copy config/switch.example.json to config/switch.json and run `make seed-switch` in the platform repo."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Realm</TableHead>
                <TableHead>Endpoints</TableHead>
                <TableHead>Outbound caller ID</TableHead>
                <TableHead>Dials out on</TableHead>
                <TableHead>Created</TableHead>
                {canManage && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => {
                const variables = (d.json_data?.variables ?? {}) as Record<string, string>;
                return (
                  <TableRow key={d.id}>
                    <MonoCell>
                      <span className="inline-flex items-center gap-1">
                        {d.name}
                        <CopyButton value={d.name} />
                      </span>
                    </MonoCell>
                    <TableCell>
                      <Badge tone={d.endpoint_count > 0 ? "neutral" : "outline"}>
                        {d.endpoint_count}
                      </Badge>
                    </TableCell>
                    <MonoCell className="text-text-muted">
                      {variables.outbound_caller_id_number ?? "—"}
                    </MonoCell>
                    <TableCell>
                      <OutboundCell domain={d} />
                    </TableCell>
                    <TableCell className="text-xs text-text-muted">
                      {formatDateTime(d.created_at)}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Actions for ${d.name}`}
                            >
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onSelect={() => setRouting(d)}>
                              <Settings2 />
                              Outbound routing
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {canManage && data && <DomainTranscriptionCard domains={data} />}
      {data && <DomainRetentionCard domains={data} canManage={canManage} />}

      <DomainOutboundDialog
        open={Boolean(routing)}
        onOpenChange={(open) => !open && setRouting(undefined)}
        domain={routing}
      />
    </Page>
  );
}

/**
 * What the switch would dial out on, not what was saved.
 *
 * The distinction matters in exactly one state and it is the state worth catching: a domain that
 * chose its trunks and has since had all of them disabled shows no trunks at all, because that
 * is what happens to its calls. Showing the saved names there would read as "configured".
 */
function OutboundCell({ domain }: { domain: Domain }) {
  const chose = domain.outbound_routing.mode === "gateways";
  if (domain.outbound_gateways.length === 0) {
    return (
      <Badge tone="danger" size="sm">
        {chose ? "No live trunk" : "No trunk"}
      </Badge>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {domain.outbound_gateways.map((g, i) => (
        <React.Fragment key={g.id}>
          {i > 0 && (
            <span className="text-xs text-text-subtle" aria-label="then">
              →
            </span>
          )}
          <span className="text-xs text-text">{g.name}</span>
        </React.Fragment>
      ))}
      {!chose && (
        <Badge tone="outline" size="sm">
          Automatic
        </Badge>
      )}
    </span>
  );
}
